import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { ClipboardCheck, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_ITEMS = [
  { label: "Leave all keys, garage openers, and access devices", completed: false },
  { label: "Remove all personal belongings from the property", completed: false },
  { label: "Clean the home (basic or professional)", completed: false },
  { label: "Remove trash and debris from the property", completed: false },
  { label: "Confirm utilities are scheduled for transfer or cancellation", completed: false },
];

export const PreCloseChecklist = ({ transactionId, initialChecklist }) => {
  const [items, setItems] = useState(() => {
    if (Array.isArray(initialChecklist) && initialChecklist.length > 0) return initialChecklist;
    return DEFAULT_ITEMS;
  });
  const [initialized, setInitialized] = useState(false);

  // If no checklist in DB, persist the defaults on first render
  useEffect(() => {
    if (!initialized && (!Array.isArray(initialChecklist) || initialChecklist.length === 0)) {
      supabase
        .from("transactions")
        .update({ pre_close_checklist: DEFAULT_ITEMS })
        .eq("id", transactionId)
        .then(() => setInitialized(true));
    } else {
      setInitialized(true);
    }
  }, []);

  const completedCount = items.filter((i) => i.completed).length;

  const toggleItem = async (index) => {
    const updated = items.map((item, i) => i === index ? { ...item, completed: !item.completed } : item);
    setItems(updated);
    try {
      const { error } = await supabase
        .from("transactions")
        .update({ pre_close_checklist: updated })
        .eq("id", transactionId);
      if (error) throw error;
    } catch (err) {
      console.error("[PreCloseChecklist] Save error:", err);
      // Revert on error
      setItems(items);
      toast.error("Failed to update checklist");
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-100 shadow-soft p-6 my-6" data-testid="preclose-checklist">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
          <ClipboardCheck className="w-[18px] h-[18px] text-slate-400" strokeWidth={1.75} />
          Pre-Close Checklist
        </h3>
        <span className="text-[13px] text-slate-500" data-testid="preclose-progress">
          {completedCount} of {items.length} completed
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded-full bg-slate-100 mb-5" data-testid="preclose-progress-bar">
        <div
          className="h-full rounded-full bg-emerald-500 transition-all duration-300"
          style={{ width: `${items.length > 0 ? (completedCount / items.length) * 100 : 0}%` }}
        />
      </div>

      {/* Checklist items */}
      <div className="space-y-0.5" data-testid="preclose-items">
        {items.map((item, index) => (
          <button
            key={index}
            onClick={() => toggleItem(index)}
            className="w-full flex items-start gap-3 py-2.5 px-1 rounded-md hover:bg-slate-50 transition-colors text-left group"
            data-testid={`preclose-item-${index}`}
          >
            {item.completed ? (
              <CheckSquare className="w-[18px] h-[18px] text-emerald-500 shrink-0 mt-0.5" />
            ) : (
              <Square className="w-[18px] h-[18px] text-slate-300 group-hover:text-slate-400 shrink-0 mt-0.5" />
            )}
            <span className={`text-sm leading-snug ${
              item.completed ? "text-slate-400 line-through" : "text-slate-700"
            }`}>
              {item.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
